import { debounce } from "@mui/material";
import { useCachedFragment } from "@sinch/hooks/useCachedFragment";
import { useLazyQueryHandler } from "@sinch/hooks/useLazyQueryHandler";
import { mapToOptions, Option } from "@sinch/utils/option";
import { includes } from "ramda";
import { ensureArray, isNotEmpty, isNotNil, isNotNilOrEmpty } from "ramda-adjunct";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompaniesData, CompaniesListItem } from "./queries.graphql";
import { CompaniesDataQuery, CompaniesDataQueryVariables, CompaniesListItemFragment } from "./queries.types";
import { AutocompleteInput } from "@sinch/components/form/AutocompleteInput/AutocompleteInput";
import type { AutocompleteInputProps } from "@sinch/components/form/AutocompleteInput/types";
import { AutocompleteOptions } from "@sinch/components/form/AutocompleteInput/types";

export const CompanySelect = <
  Multiple extends boolean | undefined = undefined,
  DisableClearable extends boolean | undefined = undefined,
  FreeSolo extends boolean | undefined = undefined,
>(
  props: Omit<AutocompleteInputProps<AutocompleteOptions, Multiple, DisableClearable, FreeSolo>, "ref" | "options">
) => {
  const { t } = useTranslation();
  const [inputVal, setInputVal] = useState<string>("");
  const { data, dispatch, loading } = useLazyQueryHandler<CompaniesDataQuery, CompaniesDataQueryVariables>(
    CompaniesData
  );

  const debounceDispatch = useCallback(debounce(dispatch, 200), [dispatch]);

  const handleSearch = (val: string) => {
    if (isNotNilOrEmpty(val)) {
      debounceDispatch({ filter: { name: { LIKE: val } } });
    }
  };

  const { data: companies, loading: initLoading } = useCachedFragment<CompaniesListItemFragment>({
    fragment: CompaniesListItem,
    type: "Company",
    ids: props.value ? ensureArray(props.value as string[]) : [],
    query: CompaniesData,
    variables: {
      filter: props.value ? { id: { IN: ensureArray(props.value) } } : null,
    },
  });

  const companyOptions: Option[] = useMemo(() => mapToOptions("id", "name")(data?.companies || []), [data]);
  const options: Option[] = useMemo(() => {
    const valueCompanies = mapToOptions("id", "name")(companies?.filter(isNotNil) || []) as Option<string, string>[];
    return [...companyOptions, ...valueCompanies];
  }, [companyOptions, companies]);

  return (
    <AutocompleteInput<AutocompleteOptions, Multiple, DisableClearable, FreeSolo>
      filterOptions={() => (isNotNilOrEmpty(inputVal) ? companyOptions : [])}
      loading={loading}
      noOptionsText={isNotEmpty(inputVal) ? undefined : t("typeToSearch")}
      openOnFocus
      placeholder={t("search")}
      skeleton={initLoading}
      {...props}
      defaultValue={props.defaultValue ?? undefined}
      inputValue={inputVal ?? ""}
      onInputChange={(event, newInputValue, reason) => {
        // prevent backfire on select
        if (!event || (props.multiple && includes(reason, ["removeOption", "selectOption"]))) {
          return;
        }

        setInputVal(newInputValue);
        handleSearch(newInputValue);
      }}
      options={options}
    />
  );
};
